import { ApolloError } from '@apollo/client';
import { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Sample, Pack } from 'types/generated/graphql';
import { TabNames } from 'types/enums';
import { NavigationParams } from 'types/params';
import { SearchResults, UpdateResult } from '../types';

type SearchResultsProps = { defaultTab?: TabNames };

const useSearchResults = (props?: SearchResultsProps): SearchResults => {
    const { search } = useLocation();

    const searchParams = new URLSearchParams(search);

    const defaultTab = props?.defaultTab ?? TabNames.Samples;
    const currentTab = searchParams.get(NavigationParams.tab)?.toLowerCase();

    const activeTab = currentTab ?? defaultTab;

    const [samplesLoading, setSamplesLoading] = useState(false);
    const [packsLoading, setPacksLoading] = useState(false);
    const [samplesCompleted, setSamplesCompleted] = useState<boolean>(false);
    const [packsCompleted, setPacksCompleted] = useState<boolean>(false);

    const [samplesError, setSamplesError] = useState<ApolloError | undefined>(
        undefined,
    );
    const [packsError, setPacksError] = useState<ApolloError | undefined>(
        undefined,
    );

    const [samplesResult, setSamplesResult] = useState<UpdateResult<Sample>>({
        items: undefined,
        total: undefined,
        filterMetaObj: undefined,
    });

    const [packsResult, setPacksResult] = useState<UpdateResult<Pack>>({
        items: undefined,
        total: undefined,
        filterMetaObj: undefined,
    });

    const updateSamplesError = useCallback(
        (error: ApolloError) => {
            setSamplesError(error);
        },
        [setSamplesError],
    );

    const updatePacksError = useCallback(
        (error: ApolloError) => {
            setPacksError(error);
        },
        [setPacksError],
    );

    const updateSamplesLoading = useCallback(
        (loading: boolean) => {
            if (loading) {
                setSamplesCompleted(false);
            }

            setSamplesLoading(loading);
        },
        [setSamplesLoading],
    );

    const updatePacksLoading = useCallback(
        (loading: boolean) => {
            if (loading) {
                setPacksCompleted(false);
            }
            setPacksLoading(loading);
        },
        [setPacksLoading],
    );

    const updateSamplesResult = useCallback(
        ({ items, total, filterMetaObj }: UpdateResult<Sample>) => {
            setSamplesResult({
                items,
                total,
                filterMetaObj,
            });
        },
        [],
    );

    const updatePacksResult = useCallback(
        ({ items, total, filterMetaObj }: UpdateResult<Pack>) => {
            setPacksResult({
                items,
                total,
                filterMetaObj,
            });
        },
        [],
    );

    const updateSamplesCompleted = useCallback(() => {
        setSamplesCompleted(true);
    }, []);

    const updatePacksCompleted = useCallback(() => {
        setPacksCompleted(true);
    }, []);

    let activeTabTotal;

    switch (activeTab) {
        case TabNames.Samples:
            activeTabTotal = samplesResult?.total;
            break;

        case TabNames.Packs:
            activeTabTotal = packsResult.total;
            break;
    }

    return {
        updateSamplesResult,
        updateSamplesLoading,
        updateSamplesError,
        updateSamplesCompleted,
        samples: samplesResult.items,
        samplesError: samplesError,
        samplesLoading: samplesLoading,
        samplesTotal: samplesResult.total,
        samplesFilterMetaObj: samplesResult.filterMetaObj,
        samplesCompleted,

        updatePacksResult,
        updatePacksLoading,
        updatePacksError,
        updatePacksCompleted,
        packs: packsResult.items,
        packsError: packsError,
        packsLoading: packsLoading,
        packsTotal: packsResult.total,
        packsFilterMetaObj: packsResult.filterMetaObj,
        packsCompleted,

        filterMetaObj: samplesResult.filterMetaObj,
        activeTabTotal,
    };
};

export default useSearchResults;
